import { samlConfig } from '../config/samlConfig';
// API URL from environment variable only
const API_URL = process.env.REACT_APP_API_URL;

// Global flags to ensure Main API is called exactly once per session
let mainApiCalled = false;
let mainApiResponse = null;

// API URL from environment variable only
class AuthService {
    constructor() {
        // No MSAL instance needed for SAML
        this.isInitialized = false;
        // Add caching mechanism for auth status check
        this.authStatusPromise = null;
        this.lastAuthCheck = null;
        this.AUTH_CACHE_DURATION = 60000; // Increase to 60 seconds cache
        // Add flag to prevent multiple simultaneous calls
        this.isCheckingAuth = false;
        
        // Initialize Main API flags from session storage if available
        if (sessionStorage.getItem('mainApiCalled') === 'true') {
            mainApiCalled = true;
            try {
                const storedResponse = sessionStorage.getItem('mainApiResponse');
                if (storedResponse) {
                    mainApiResponse = JSON.parse(storedResponse);
                }
            } catch (error) {
                console.warn('Error parsing stored Main API response:', error);
            }
        }
    }
async initialize() {
        this.isInitialized = true;
        // Try sessionStorage.user first (set during current tab's auth flow).
        // If missing (new tab after tab close), reconstruct from the persisted split:
        //   localStorage.userIdentity  → userId / userName / roles
        //   sessionStorage.memberContext → memberId / cid / caseId / systemId
        let userData = null;
        try {
            const raw = sessionStorage.getItem('user');
            if (raw) userData = JSON.parse(raw);
        } catch (_) {}

        if (!userData) {
            try {
                const identity  = JSON.parse(localStorage.getItem('userIdentity')  || '{}');
                const memberCtx = JSON.parse(sessionStorage.getItem('memberContext') || '{}');
                if (identity.userId) {
                    userData = { ...identity, ...memberCtx };
                }
            } catch (_) {}
        }

        if (userData && sessionStorage.getItem('isAuthenticated') === 'true') {
            try {
                this.lastAuthCheck = {
                    result: { isAuthenticated: true, user: userData },
                    timestamp: Date.now()
                };
            } catch (error) {
                console.error('Error priming auth cache during initialization:', error);
            }
        }
    }
    async login() {
        // SAML login will be handled by redirecting to the SAML login page
        window.location.href = '/';
    }
async logout() {
        try {
            // Clear session storage
            sessionStorage.removeItem('isAuthenticated');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('mainApiCalled');
            sessionStorage.removeItem('mainApiResponse');
            sessionStorage.removeItem('memberContext');
            sessionStorage.removeItem('samlRequestParams');

            // Clear localStorage keys
            localStorage.removeItem('userIdentity');
            localStorage.removeItem('samlRequestParams'); // legacy key cleanup
            
            // Reset Main API flags
            mainApiCalled = false;
            mainApiResponse = null;
            
            // Clear authentication status cache
            this.clearAuthStatusCache();
                        // For SAML, redirect to SLO if configured
            if (samlConfig.sloUrl && !samlConfig.sloUrl.includes('your-idp.com')) {
                window.location.href = samlConfig.sloUrl;
            } else {
                // Fallback to local logout
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Error during logout:', error);
            throw error;
        }
    }
    async getAccessToken() {
        // For cookie-based authentication, we don't need to manually get the token
        // The browser will automatically include the HTTP-only cookie in requests
        // Return a placeholder indicating cookie-based auth is being used
        return 'cookie-based-auth';
    }
    async acquireTokenForAPI() {
        // Same as getAccessToken for SAML implementation
        return this.getAccessToken();
    }
    async getCurrentUser() {
        try {
            const user = sessionStorage.getItem('user');
            if (!user) {
                throw new Error('No authenticated user found');
            }
            return JSON.parse(user);
        } catch (error) {
            console.error('Error getting current user:', error);
            throw error;
        }
    }
    // Utility function to get cookie value by name
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }
    // Get auth token from cookies
    getAuthTokenFromCookie() {
        const token = this.getCookie('auth_token');
        return token;
    }
async callMainAPI(requestParams) {
        // Check if Main API has already been called successfully in this session.
        // Before returning the cache, verify the member context in the URL hasn't changed
        // (e.g. the same tab navigating to a deep-link for a different member).
        if (mainApiCalled && mainApiResponse) {
            const urlCheck   = new URLSearchParams(window.location.search);
            const urlMember  = urlCheck.get('MemberID') || urlCheck.get('memberId') || '';
            const urlCid     = urlCheck.get('CID')      || urlCheck.get('cid')      || '';
            const urlCaseId  = urlCheck.get('CaseID')   || urlCheck.get('caseId')   || '';
            // Compare against sessionStorage.memberContext (the current tab's member context)
            const ctx = JSON.parse(sessionStorage.getItem('memberContext') || '{}');
            const memberChanged = urlMember  && urlMember  !== (ctx.memberId || '');
            const cidChanged    = urlCid     && urlCid     !== (ctx.cid      || '');
            const caseChanged   = urlCaseId  && urlCaseId  !== (ctx.caseId   || '');
            if (!memberChanged && !cidChanged && !caseChanged) {
                console.log('Main API already called in this session. Returning cached response.');
                return mainApiResponse;
            }
            // Member context changed — reset cache so we re-call with the new params
            console.log('Member context changed in URL — resetting Main API cache.');
            mainApiCalled = false;
            mainApiResponse = null;
            sessionStorage.removeItem('mainApiCalled');
            sessionStorage.removeItem('mainApiResponse');
        }
        
        // First check if user is authenticated
        const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated) {
            console.log('User not authenticated. Main API call skipped.');
            return null;
        }
        
        // Get parameters — resolved with the new storage split:
        //   userId       → URL  >  localStorage.userIdentity  (persists across tab closes)
        //   member params→ URL  >  sessionStorage.memberContext (tab-scoped, fresh per tab)
        if (!requestParams) {
            const urlParams   = new URLSearchParams(window.location.search);
            const urlUserID   = urlParams.get('UserID')   || urlParams.get('userId')   || urlParams.get('userID')   || '';
            const urlMemberId = urlParams.get('MemberID') || urlParams.get('memberId') || urlParams.get('MemberId') || '';
            const urlSystemId = urlParams.get('SystemID') || urlParams.get('systemId') || urlParams.get('SystemId') || '';
            const urlCaseId   = urlParams.get('CaseID')   || urlParams.get('caseId')   || urlParams.get('CaseId')   || '';
            const urlCid      = urlParams.get('CID')      || urlParams.get('cid')      || '';

            // Resolve userId: URL first, then localStorage.userIdentity
            let resolvedUserId = urlUserID;
            if (!resolvedUserId) {
                try {
                    const identity = JSON.parse(localStorage.getItem('userIdentity') || '{}');
                    resolvedUserId = identity.userId || '';
                } catch (_) {}
            }
            // Last resort: sessionStorage.user
            if (!resolvedUserId) {
                try {
                    resolvedUserId = JSON.parse(sessionStorage.getItem('user') || '{}').userId || '';
                } catch (_) {}
            }

            if (!resolvedUserId) {
                console.warn('No userId available — cannot call Main API');
                return null;
            }

            // Resolve member params: URL always wins (fresh per tab), then sessionStorage.memberContext
            let memberId = urlMemberId, cid = urlCid, caseId = urlCaseId, systemId = urlSystemId;

            if (urlMemberId || urlCid || urlCaseId) {
                // Fresh member params in URL — update sessionStorage.memberContext for this tab
                const newCtx = { memberId, cid, caseId, systemId };
                sessionStorage.setItem('memberContext', JSON.stringify(newCtx));
                // Refresh sessionStorage.user with the updated member context
                try {
                    const existingUser = JSON.parse(sessionStorage.getItem('user') || '{}');
                    sessionStorage.setItem('user', JSON.stringify({
                        ...existingUser, userId: resolvedUserId, memberId, cid, caseId, systemId
                    }));
                } catch (_) {}
            } else {
                // No member params in URL (e.g. menu tab switch) — use tab's memberContext
                try {
                    const ctx = JSON.parse(sessionStorage.getItem('memberContext') || '{}');
                    memberId = ctx.memberId || '';
                    cid      = ctx.cid      || '';
                    caseId   = ctx.caseId   || '';
                    systemId = ctx.systemId || '';
                } catch (_) {}
                // Last resort: samlRequestParams (session-only relay-state fallback)
                if (!memberId && !cid && !caseId) {
                    try {
                        const relay = JSON.parse(sessionStorage.getItem('samlRequestParams') || '{}');
                        memberId = relay.memberId || ''; cid = relay.cid || relay.CID || '';
                        caseId   = relay.caseId   || ''; systemId = relay.systemId || '';
                    } catch (_) {}
                }
            }

            requestParams = { userID: resolvedUserId, memberId, cid, caseId, systemId };
            console.log('Resolved Main API params:', requestParams);
        }
        
        try {
            // Get auth token from cookie
            const authToken = this.getAuthTokenFromCookie();
            
            // Basic headers
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Add token if available
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            console.log('Making authenticated Main API call (first and only call)');
            
            // Simple API call
            const response = await fetch(`${API_URL}/Main/Main`, {
                method: 'POST',
                credentials: 'include',
                headers: headers,
                body: JSON.stringify(requestParams)
            });
            
            if (!response.ok) {
                console.error(`Main API call failed with status: ${response.status}`);
                return null;
            }
            
            // Get response
            const result = await response.json();
            
            // Save token for future API calls
            const newAuthToken = this.getAuthTokenFromCookie();
            if (newAuthToken) {
                sessionStorage.setItem('authToken', newAuthToken);
            }
            
            // Mark that Main API has been called successfully and cache the response
            mainApiCalled = true;
            mainApiResponse = result;
            
            // Also store in session storage to persist across page reloads
            try {
                sessionStorage.setItem('mainApiCalled', 'true');
                sessionStorage.setItem('mainApiResponse', JSON.stringify(result));
            } catch (storageError) {
                console.warn('Could not store Main API response in session storage:', storageError);
            }
            
            return result;
        } catch (error) {
            console.error('Error in Main API call:', error);
            return null;
        }
    }

    async getServerInfo() {
        // Return empty server info without making an API call
        return { serverId: '', serverName: '' };
    }

    // Track ReportList API calls in this session with menuId-specific caching
    reportListApiCalled = false;
    reportListData = {};  // Changed to object to store data by menuId
    reportListMenuIds = new Set(); // Track which menuIds have been cached
    
    async callMainAPIAndLoadReports(requestParams, menuId, forceRefresh = false) {
        // Check authentication first
        const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated) {
            console.log('User not authenticated. API calls skipped.');
            return null;
        }

        // Call Main API after authentication
        const mainApiResponse = await this.callMainAPI(requestParams);

        // Create a response object that will include both main API data and reports data
        const responseWithReports = { ...mainApiResponse, reportsData: null };

        // Convert menuId to string for consistent comparison
        const menuIdKey = menuId !== undefined && menuId !== null ? menuId.toString() : '0';

        // Only call ReportList API if we haven't already for this menuId in this session, or if forceRefresh is true
        if (forceRefresh || !this.reportListApiCalled || !this.reportListData[menuIdKey]) {
            // Check if forceRefresh is true
            if (forceRefresh) {
                console.log(`🔄 Force refresh requested for menuId ${menuIdKey}. Making fresh API call.`);
                // Delete any existing cache for this menuId
                delete this.reportListData[menuIdKey];
            }
            // Check if we already have cached ReportList data for this specific menuId
            else if (this.reportListApiCalled && this.reportListData[menuIdKey]) {
                console.log(`ReportList API was already called for menuId ${menuIdKey}. Using cached data.`);
                responseWithReports.reportsData = this.reportListData[menuIdKey];
                return responseWithReports;
            }
            
            // Only proceed if Main API call was successful
            if (mainApiResponse) {
                try {
                    // Get user data from session storage
                    const userDataStr = sessionStorage.getItem('user');
                    if (userDataStr) {
                        const userData = JSON.parse(userDataStr);
                        
                        // Import reportService to call ReportList API
                        const reportService = await import('./reportService');
                        
                        // Check if user is admin - first from Main API response, then fall back to user roles
                        let isAdmin;
                        if (mainApiResponse?.isAdmin !== undefined) {
                            // Use isAdmin directly from Main API response
                            isAdmin = mainApiResponse.isAdmin === true || mainApiResponse.isAdmin === "true";
                            console.log('Using isAdmin from Main API response:', isAdmin);
                        } else {
                            // Fall back to checking user roles
                            isAdmin = userData.roles && 
                                (userData.roles.includes('admin') || userData.roles.includes('Admin'));
                            console.log('Using isAdmin from user roles:', isAdmin);
                        }
                        const adminUser = isAdmin ? 'true' : 'false';
                        
                        // Extract userId and menuId directly from the Main API response first
                        // This ensures we use exactly the same values as the /main API used
                        let userId = mainApiResponse?.userId || mainApiResponse?.UserId;
                        
                        // Use the menuId passed as separate parameter
                        // This allows us to use a different menuId for the reports API than what's in the Main API
                        
                        console.log('Values for API call:', { userId, menuId, source: menuId ? 'from separate parameter' : 'not set' });
                        
                        // If Main API response doesn't have userId or menuId, use session data as fallback
                        if (!userId) {
                            userId = userData.userId || userData.UserId || userData.userID || userData.UserID;
                            console.log('Using userId from session data instead:', userId);
                        }
                        
                        if (menuId === undefined || menuId === null) {
                            // Try to get menuId from URL query parameters
                            const urlParams = new URLSearchParams(window.location.search);
                            menuId = urlParams.get('menuId') || 0;
                            console.log('Using menuId from URL or default:', menuId);
                        } else {
                            console.log('Using explicitly provided menuId:', menuId);
                        }
                        
                        // Ensure menuId is treated as a number
                        menuId = Number(menuId);
                        
                        console.log('🌐 Fetching reports list after successful Main API call with menuId:', menuId);
                        
                        // Get the auth token from cookie
                        const authToken = this.getAuthTokenFromCookie();
                        
                            // Make a single call to the ReportList API with session token
                            try {
                                // Always make a fresh API call when fetching reports to ensure we get the latest data
                                console.log('📡 Making ReportList API call with parameters:', { userId, menuId, adminUser });
                                
                                // Use the correct API endpoint and pass parameters 
                                const reportListUrl = `${API_URL}/ReportList?userId=${userId}&adminUser=${adminUser}&menuId=${menuId}`;
                                console.log('🔗 ReportList API URL:', reportListUrl);
                            
                            const response = await fetch(reportListUrl, {
                                method: 'GET',
                                credentials: 'include',
                                headers: {
                                    'Authorization': authToken ? `Bearer ${authToken}` : '',
                                    'Content-Type': 'application/json',
                                    'Cache-Control': 'no-cache'
                                }
                            });
                            
                            if (response.ok) {
                                const data = await response.json();
                                console.log('✅ ReportList API call successful:', data);
                                
                                // Process the response
                                const reportsData = {
                                    reports: data.Reports || data.reports || [],
                                    showKeywordTags: data.ShowKeywordTags || data.showKeywordTags || true
                                };
                                
                                // Define cacheKey for storing the results
                                const cacheKey = `${userId}-${adminUser}-${menuId || 0}`;
                                const menuIdKey = menuId !== undefined && menuId !== null ? menuId.toString() : '0';
                                console.log('💾 Caching reports with key:', cacheKey, 'for menuId:', menuIdKey);
                                
                                // Save to session storage for future use
                                try {
                                    sessionStorage.setItem(`reports_cache_${cacheKey}`, JSON.stringify(reportsData));
                                } catch (storageError) {
                                    console.warn('Could not store reports in session storage:', storageError);
                                }
                                
                                // Cache in memory for this session with menuId-specific cache
                                this.reportListApiCalled = true;
                                this.reportListData[menuIdKey] = reportsData;
                                this.reportListMenuIds.add(menuIdKey);
                                
                                // Add to response
                                responseWithReports.reportsData = reportsData;
                            } else {
                                console.warn('ReportList API call failed with status:', response.status);
                                responseWithReports.reportsData = { reports: [], showKeywordTags: true };
                            }
                        } catch (error) {
                            console.error('Error calling ReportList API:', error);
                            responseWithReports.reportsData = { reports: [], showKeywordTags: true };
                        }
                    }
                } catch (error) {
                    console.warn('Error loading reports:', error);
                }
            }
        } else {
            // Use cached data
            console.log(`ReportList API was already called for menuId ${menuIdKey}. Using cached data.`);
            responseWithReports.reportsData = this.reportListData[menuIdKey];
        }

        return responseWithReports;
    }
    isAuthenticated() {
        // Only check sessionStorage for initial client-side check
        // The real authentication check should be done via API call
        const isAuth = sessionStorage.getItem('isAuthenticated');
        return isAuth === 'true';
    }
// New method to check authentication status via API (cookie-based JWT)
    // Modified implementation to allow exactly two calls:
    // 1. Before SAML login redirection
    // 2. After returning to reports-list page
    async checkAuthenticationStatus() {
        // Track API calls with specific context flags to allow two specific calls
        // Use sessionStorage instead of localStorage so flags reset when tab is closed
        const AUTH_CALL_BEFORE_SAML = 'authCallBeforeSaml';
        const AUTH_CALL_AFTER_REDIRECT = 'authCallAfterRedirect';
        // Get current URL path to determine context
        const currentPath = window.location.pathname;
        // Determine if we're in one of the two allowed call contexts
        const isSamlLoginPage = currentPath === '/' || currentPath.includes('/login');
        const isReportsListPage = currentPath.includes('/reports-list');
        
        // Check if the respective call has already been made
        const samlCallMade = sessionStorage.getItem(AUTH_CALL_BEFORE_SAML) === 'true';
        const redirectCallMade = sessionStorage.getItem(AUTH_CALL_AFTER_REDIRECT) === 'true';
        // Allow call before SAML login if we're on login page and that call hasn't been made yet
        if (isSamlLoginPage && !samlCallMade) {
            sessionStorage.setItem(AUTH_CALL_BEFORE_SAML, 'true');
            return this.makeAuthStatusApiCall();
        }
                // Allow call after redirect if we're on reports page and that call hasn't been made yet
        if (isReportsListPage && !redirectCallMade) {
            sessionStorage.setItem(AUTH_CALL_AFTER_REDIRECT, 'true');
            return this.makeAuthStatusApiCall();
        }
        // For all other cases, use session data and don't call API
        
        // First check sessionStorage for persistent authentication state
        const isAuthInSession = sessionStorage.getItem('isAuthenticated') === 'true';
        const authUserData = sessionStorage.getItem('user');
        if (isAuthInSession && authUserData) {
            try {
                return {
                    isAuthenticated: true,
                    user: JSON.parse(authUserData)
                };
            } catch (error) {
                console.error('Error parsing user data from session storage:', error);
            }
        }
        // If not authenticated in session but we have a cached result, use it
        if (this.lastAuthCheck) {
            return this.lastAuthCheck.result;
        }
                // If no cache, return not authenticated without API call
        return { isAuthenticated: false, user: null };
    }
        // Helper method to make the actual API call and handle caching
    async makeAuthStatusApiCall() {
        // If we're already checking auth, return the existing promise
        if (this.isCheckingAuth === true && typeof this.authStatusPromise === 'object' && typeof this.authStatusPromise.then === 'function') {
            return this.authStatusPromise;
        }
        // If we have a recent cached result, return it (no new API call)
        // Only use cache if we're not on the reports list page to ensure fresh data after SAML flow
        const currentPath = window.location.pathname;
        const isReportsListPage = currentPath.includes('/reports-list');
                if (!isReportsListPage && this.lastAuthCheck && 
            (Date.now() - this.lastAuthCheck.timestamp < this.AUTH_CACHE_DURATION)) {
            return this.lastAuthCheck.result;
        }
// This is an allowed API call, proceed
        // Set flag to indicate we're checking auth
        this.isCheckingAuth = true;
        // Create a new auth status check promise
        this.authStatusPromise = this.performAuthStatusCheck();
        try {
            const result = await this.authStatusPromise;
            // Cache the result with timestamp
            this.lastAuthCheck = {
                result,
                timestamp: Date.now()
            };
            
            // If authenticated, save to session storage
            if (result.isAuthenticated) {
                sessionStorage.setItem('isAuthenticated', 'true');
                if (result.user) {
                    sessionStorage.setItem('user', JSON.stringify(result.user));
                }
            }
                        return result;
        } catch (error) {
            console.error('Auth status check failed:', error);
            return { isAuthenticated: false };
        } finally {
            // Clear the checking flag and promise
            this.isCheckingAuth = false;
            this.authStatusPromise = null;
        }
    }
// Separate method to perform the actual API call
    async performAuthStatusCheck() {
        try {
            const apiUrl = `${API_URL}/Login/check-auth-status`;
            
            const response = await fetch(apiUrl, {
                method: 'GET',
                credentials: 'include', // Include cookies in the request
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache' // Prevent caching
                }
            });
            if (!response.ok) {
                return { isAuthenticated: false };
            }
const data = await response.json();
            const isAuthenticated = data?.IsAuthenticated || data?.isAuthenticated || false;
            if (isAuthenticated) {
                sessionStorage.setItem('isAuthenticated', 'true');
                const resolvedUserId = data?.UserId || data?.userId || '';

                // User identity → localStorage (survives tab close; no re-auth needed on re-entry)
                const userIdentity = {
                    userId:   resolvedUserId,
                    userName: data?.Username || data?.username || '',
                    email:    data?.Email    || data?.email    || '',
                    roles:    data?.Roles    || data?.roles    || [],
                    isAuthenticated: true
                };
                localStorage.setItem('userIdentity', JSON.stringify(userIdentity));

                // Member context lives in sessionStorage.memberContext — do NOT overwrite it here.
                // It was set from URL params at the start of this tab's session and must remain
                // tab-scoped so each tab can carry different member context.
                const memberCtx = JSON.parse(sessionStorage.getItem('memberContext') || '{}');

                // Rebuild sessionStorage.user for backward compat (identity + tab's member context)
                const userInfo = {
                    ...userIdentity,
                    memberId: memberCtx.memberId || '',
                    cid:      memberCtx.cid      || '',
                    caseId:   memberCtx.caseId   || '',
                    systemId: memberCtx.systemId || ''
                };
                sessionStorage.setItem('user', JSON.stringify(userInfo));
            } else {
                // Clear sessionStorage if not authenticated
                sessionStorage.removeItem('isAuthenticated');
                sessionStorage.removeItem('user');
            }
            return { 
                isAuthenticated, 
                user: isAuthenticated ? JSON.parse(sessionStorage.getItem('user')) : null 
            };
        } catch (error) {
            console.error('❌ Error checking authentication status:', error);
            // Clear sessionStorage on error
            sessionStorage.removeItem('isAuthenticated');
            sessionStorage.removeItem('user');
            throw error; // Re-throw to be handled by caller
        }
    }
// Method to clear cached auth status (useful for logout or when auth status changes)
    clearAuthStatusCache() {
        this.lastAuthCheck = null;
        this.authStatusPromise = null;
        this.isCheckingAuth = false;
            // Reset the API call flags on logout to allow new checks on next login cycle
        sessionStorage.removeItem('authCallBeforeSaml');
        sessionStorage.removeItem('authCallAfterRedirect');
    }
    // Method to force refresh auth status (bypasses cache but still respects the allowed API call contexts)
    async forceRefreshAuthStatus() {
        // Clear local cache
        this.lastAuthCheck = null;
        this.authStatusPromise = null;
        this.isCheckingAuth = false;
        // Get current URL path to determine context
        const currentPath = window.location.pathname;
        const isSamlLoginPage = currentPath === '/' || currentPath.includes('/login');
        const isReportsListPage = currentPath.includes('/reports-list');
        // Check if the respective call has already been made
        const samlCallMade = sessionStorage.getItem('authCallBeforeSaml') === 'true';
        const redirectCallMade = sessionStorage.getItem('authCallAfterRedirect') === 'true';
        // Only allow API calls in the two specific contexts if they haven't been made yet
        if ((isSamlLoginPage && !samlCallMade) || (isReportsListPage && !redirectCallMade)) {
            return this.checkAuthenticationStatus();
        }
        // Check session storage first
        const isAuthInSession = sessionStorage.getItem('isAuthenticated') === 'true';
        const authUserData = sessionStorage.getItem('user');
        if (isAuthInSession && authUserData) {
            try {
                return {
                    isAuthenticated: true,
                    user: JSON.parse(authUserData)
                };
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }       
        // If not in session, return unauthenticated
        return { isAuthenticated: false, user: null };
    }
}
const authServiceInstance = new AuthService();
export const getCurrentUser = () => authServiceInstance.getCurrentUser();
export const callMainAPI = (params) => authServiceInstance.callMainAPI(params);
export const callMainAPIAndLoadReports = (params, menuId, forceRefresh) => authServiceInstance.callMainAPIAndLoadReports(params, menuId, forceRefresh);
export const getServerInfo = () => authServiceInstance.getServerInfo();
export const checkAuthenticationStatus = () => authServiceInstance.checkAuthenticationStatus();
export const clearAuthStatusCache = () => authServiceInstance.clearAuthStatusCache();
export const forceRefreshAuthStatus = () => authServiceInstance.forceRefreshAuthStatus();
export const authService = authServiceInstance;
export default authServiceInstance;